import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../context/I18nContext';
import { formatEuDateTime } from '../utils/dateFormat';
import './AuditLog.css';

const ACTION_META = {
  LOGIN:                    { label: 'Login',                  color: '#6b7280', category: 'auth' },
  LOGIN_PASSKEY:            { label: 'Passkey Login',          color: '#8b5cf6', category: 'auth' },
  LOGIN_DISCORD:            { label: 'Discord Login',          color: '#5865f2', category: 'auth' },
  LOGOUT:                   { label: 'Logout',                 color: '#9ca3af', category: 'auth' },
  PASSKEY_REMOVED:          { label: 'Passkey Removed',        color: '#ef4444', category: 'auth' },
  PASSWORD_CHANGED:         { label: 'Password Changed',       color: '#f59e0b', category: 'auth' },
  USER_CREATED:             { label: 'User Created',           color: '#10b981', category: 'users' },
  USER_UPDATED:             { label: 'User Updated',           color: '#3b82f6', category: 'users' },
  USER_DELETED:             { label: 'User Deleted',           color: '#ef4444', category: 'users' },
  MONEY_GIVEN:              { label: 'Money Given',            color: '#10b981', category: 'economy' },
  MONEY_TAKEN:              { label: 'Money Taken',            color: '#ef4444', category: 'economy' },
  JOB_CREATED:              { label: 'Job Created',            color: '#10b981', category: 'jobs' },
  JOB_UPDATED:              { label: 'Job Updated',            color: '#3b82f6', category: 'jobs' },
  JOB_REMOVED:              { label: 'Job Removed',            color: '#ef4444', category: 'jobs' },
  JOB_DELETED:              { label: 'Job Deleted',            color: '#ef4444', category: 'jobs' },
  JOB_SET_PRIMARY:          { label: 'Primary Job Set',        color: '#10b981', category: 'jobs' },
  JOB_GRADE_UPDATED:        { label: 'Grade Updated',          color: '#3b82f6', category: 'jobs' },
  JOB_GRADE_DELETED:        { label: 'Grade Deleted',          color: '#ef4444', category: 'jobs' },
  DONATE_CODE_GENERATED:    { label: 'Code Generated',         color: '#8b5cf6', category: 'donate' },
  DONATE_CODE_DELETED:      { label: 'Code Deleted',           color: '#ef4444', category: 'donate' },
  ITEM_UPDATED:             { label: 'Item Updated',           color: '#f59e0b', category: 'items' },
  VEHICLE_DELETED:          { label: 'Vehicle Deleted',        color: '#ef4444', category: 'vehicles' },
  CHARACTER_MOVED:          { label: 'Character Moved',        color: '#8b5cf6', category: 'characters' },
  CHARACTER_PED_SET:        { label: 'Ped Model Set',          color: '#3b82f6', category: 'characters' },
  CHARACTER_DELETED:        { label: 'Character Deleted',      color: '#ef4444', category: 'characters' },
  PHONE_RENAMED:            { label: 'Phone Number Changed',   color: '#14b8a6', category: 'phones' },
  COMPANY_DEPOSIT:          { label: 'Company Deposit',        color: '#10b981', category: 'companies' },
  COMPANY_WITHDRAW:         { label: 'Company Withdraw',       color: '#ef4444', category: 'companies' },
  COMPANY_SCHEDULE_CREATED: { label: 'Schedule Created',       color: '#14b8a6', category: 'companies' },
  COMPANY_SCHEDULE_UPDATED: { label: 'Schedule Updated',       color: '#3b82f6', category: 'companies' },
  COMPANY_SCHEDULE_DELETED: { label: 'Schedule Deleted',       color: '#f59e0b', category: 'companies' },
  HEIST_GIVE_XP:            { label: 'Heist XP Given',         color: '#10b981', category: 'heists' },
  HEIST_GIVE_CREDITS:       { label: 'Heist Credits Given',    color: '#10b981', category: 'heists' },
  HEIST_REMOVE_CREDITS:     { label: 'Heist Credits Removed',  color: '#ef4444', category: 'heists' },
  REFUND_ITEMS_GIVEN:       { label: 'Refund Items Given',    color: '#10b981', category: 'refund' },
  REFUND_CACHE_REFRESH:     { label: 'Refund Cache Refreshed', color: '#3b82f6', category: 'refund' },
  refund_items:             { label: 'Refund Items Given',    color: '#10b981', category: 'refund' },
  'refund.cache.refresh':   { label: 'Refund Cache Refreshed', color: '#3b82f6', category: 'refund' },
};

const CATEGORIES = [
  { id: 'all',        label: 'All' },
  { id: 'auth',       label: 'Auth' },
  { id: 'users',      label: 'Users' },
  { id: 'economy',    label: 'Economy' },
  { id: 'donate',     label: 'Donate' },
  { id: 'items',      label: 'Items' },
  { id: 'jobs',       label: 'Jobs' },
  { id: 'vehicles',   label: 'Vehicles' },
  { id: 'characters', label: 'Characters' },
  { id: 'phones',     label: 'Phones' },
  { id: 'companies',  label: 'Company Finance' },
  { id: 'heists',     label: 'Heists' },
  { id: 'refund',     label: 'Refund' },
];

const PAGE_SIZE = 50;

function formatDetails(action, details) {
  if (!details || typeof details !== 'object') return null;

  switch (action) {
    case 'LOGIN':
    case 'LOGIN_DISCORD':
      return details.username ? `${details.username}` : null;

    case 'MONEY_GIVEN':
    case 'MONEY_TAKEN': {
      const prev = details.previousBalance != null ? `$${Number(details.previousBalance).toLocaleString()}` : null;
      const next = details.newBalance != null ? `$${Number(details.newBalance).toLocaleString()}` : null;
      const amt = details.amount != null ? `$${Number(details.amount).toLocaleString()}` : null;
      const type = details.type ? ` (${details.type})` : '';
      const id = details.identifier ? ` — ${details.identifier}` : '';
      const balance = prev && next ? ` | ${prev} → ${next}` : '';
      return `${amt}${type}${id}${balance}`;
    }

    case 'DONATE_CODE_GENERATED':
      return details.code ? `${details.code} — ${details.amount} credits` : null;

    case 'DONATE_CODE_DELETED':
      return details.code ? `${details.code} (${details.credit} credits)` : null;

    case 'ITEM_UPDATED': {
      const storageLabel = details.storageType === 'player'
        ? `player ${details.storageId}`
        : `${details.storageType} ${details.storageId}`;
      return `${details.item} — ${details.action} ${details.amount} on ${storageLabel} (${details.previousCount} → ${details.newCount})`;
    }

    case 'JOB_UPDATED':
      return `${details.identifier} — job: ${details.job}, grade: ${details.grade}`;

    case 'JOB_REMOVED':
      return `${details.identifier} — removed job: ${details.job}`;

    case 'JOB_SET_PRIMARY':
      return `${details.identifier} — ${details.job} (grade ${details.grade})`;

    case 'VEHICLE_DELETED':
      return details.plate ? `Plate: ${details.plate}` : null;

    case 'USER_CREATED':
      return details.username ? `${details.username} (${details.role})` : null;

    case 'USER_UPDATED':
      return details.updates && Array.isArray(details.updates)
        ? `Changed: ${details.updates.join(', ')}`
        : null;

    case 'USER_DELETED':
      return details.username ? `${details.username}` : null;

    case 'CHARACTER_MOVED':
      return `${details.fromLicense} → ${details.toLicense} (${details.recordsUpdated} records)`;

    case 'CHARACTER_PED_SET':
      return `${details.identifier} — ${details.previousModel} → ${details.newModel}`;

    case 'PHONE_RENAMED':
      return `${details.oldPhoneNumber} → ${details.newPhoneNumber}`;

    case 'COMPANY_DEPOSIT':
    case 'COMPANY_WITHDRAW': {
      const prev = details.previousBalance != null ? `${Number(details.previousBalance).toLocaleString()}` : null;
      const next = details.newBalance != null ? `${Number(details.newBalance).toLocaleString()}` : null;
      const amount = details.amount != null ? `${Number(details.amount).toLocaleString()}` : null;
      const company = details.company || details.company_label || details.company_name || 'Company';
      const balance = prev && next ? ` | ${prev} → ${next}` : '';
      return `${company} — ${amount}${balance}`;
    }

    case 'COMPANY_SCHEDULE_CREATED':
      return `${details.company_label || details.company_name || 'Company'} — ${details.amount} scheduled`;

    case 'COMPANY_SCHEDULE_UPDATED':
      return `${details.before?.company_label || details.before?.company_name || details.after?.company_label || details.after?.company_name || 'Company'} — schedule updated`;

    case 'COMPANY_SCHEDULE_DELETED':
      return `${details.deletedSchedule?.company_label || details.deletedSchedule?.company_name || 'Company'} — schedule deleted`;

    case 'CHARACTER_DELETED': {
      const name = details.identifier || '';
      const rows = details.rowsDeleted != null ? ` (${details.rowsDeleted} rows)` : '';
      return `${name}${rows}`;
    }

    case 'JOB_CREATED':
      return details.jobId ? `Job: ${details.jobId} (${details.label})` : null;

    case 'JOB_DELETED':
      return details.jobName ? `Job: ${details.jobName}` : null;

    case 'JOB_GRADE_UPDATED':
      return details.jobName ? `${details.jobName} Grade ${details.grade}: ${details.name}` : null;

    case 'JOB_GRADE_DELETED':
      return details.jobName ? `${details.jobName} Grade ${details.grade}` : null;

    case 'LOGIN_PASSKEY':
      return details.username ? `${details.username}` : null;

    case 'LOGOUT':
      return null;

    case 'PASSKEY_REMOVED':
      return details.credentialId ? `Credential: ${details.credentialId.substring(0, 16)}...` : null;

    case 'HEIST_GIVE_XP':
      return details.identifier ? `${details.identifier} +${Number(details.amount).toLocaleString()} XP` : null;

    case 'HEIST_GIVE_CREDITS':
      return details.identifier ? `${details.identifier} +${Number(details.amount).toLocaleString()} Credits` : null;

    case 'HEIST_REMOVE_CREDITS':
      return details.identifier ? `${details.identifier} -${Number(details.amount).toLocaleString()} Credits` : null;

    case 'REFUND_ITEMS_GIVEN': {
      const target = details.target ? ` — ${details.target}` : '';
      const count = details.itemCount != null ? `${details.itemCount} items` : 'refund items';
      const total = details.totalQuantity != null ? ` (${details.totalQuantity} total)` : '';
      return `${count}${target}${total}`;
    }

    case 'REFUND_CACHE_REFRESH': {
      const fetched = details.fetched != null ? `${details.fetched} fetched` : 'cache refreshed';
      const inserted = details.inserted != null ? `, ${details.inserted} inserted` : '';
      const updated = details.updated != null ? `, ${details.updated} updated` : '';
      const deleted = details.deleted != null ? `, ${details.deleted} deleted` : '';
      return `${fetched}${inserted}${updated}${deleted}`;
    }

    default:
      return null;
  }
}

function formatTimestamp(ts) {
  return formatEuDateTime(ts);
}

function AuditLog() {
  const { apiFetch } = useAuth();
  const { t } = useI18n();

  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [category, setCategory] = useState('all');
  const [expandedRow, setExpandedRow] = useState(null);

  // categoryActions removed — not used.

  const fetchLogs = useCallback(async (pageNum, cat) => {
    setLoading(true);
    setError(null);
    try {
      const offset = pageNum * PAGE_SIZE;
      const params = new URLSearchParams({ limit: PAGE_SIZE, offset });

      const actions = cat === 'all'
        ? null
        : Object.entries(ACTION_META)
            .filter(([, meta]) => meta.category === cat)
            .map(([action]) => action);

      // The API supports single action filter; for categories with multiple actions
      // we fetch all and filter client-side for this page only.
      const url = `/api/audit-logs?${params.toString()}`;
      const response = await apiFetch(url);

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Failed to load audit logs');
      }

      const data = await response.json();
      const allLogs = data.logs || [];
      const filtered = actions ? allLogs.filter(l => actions.includes(l.action)) : allLogs;
      setLogs(filtered);
      setTotal(actions ? data.total : data.total);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [apiFetch]);

  useEffect(() => {
    fetchLogs(page, category);
  }, [fetchLogs, page, category]);

  const handleCategoryChange = (cat) => {
    setCategory(cat);
    setPage(0);
    setExpandedRow(null);
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="audit-log">
      <div className="audit-header">
        <div className="audit-header-content">
          <span className="audit-header-icon"></span>
          <div>
            <h1>{t('auditLog.title')}</h1>
            <p>{t('auditLog.subtitle')}</p>
          </div>
        </div>
        <button
          className="audit-refresh-btn"
          onClick={() => fetchLogs(page, category)}
          disabled={loading}
        >
          {loading ? `${t('auditLog.loading')}` : `${t('auditLog.refresh')}`}
        </button>
      </div>

      <div className="audit-filters">
        {CATEGORIES.map((cat) => {
          const categoryLabel = t(`auditLog.categories.${cat.id}`);
          const displayLabel = categoryLabel.startsWith('auditLog.categories.') ? cat.label : categoryLabel;

          return (
            <button
              key={cat.id}
              className={`audit-filter-btn${category === cat.id ? ' active' : ''}`}
              onClick={() => handleCategoryChange(cat.id)}
            >
              {displayLabel}
            </button>
          );
        })}
      </div>

      {error && (
        <div className="audit-error">{error}</div>
      )}

      <div className="audit-table-wrapper">
        <table className="audit-table">
          <thead>
            <tr>
              <th>{t('auditLog.table.time')}</th>
              <th>{t('auditLog.table.admin')}</th>
              <th>{t('auditLog.table.action')}</th>
              <th>{t('auditLog.table.details')}</th>
              <th>{t('auditLog.table.ip')}</th>
            </tr>
          </thead>
          <tbody>
            {loading && logs.length === 0 && (
              <tr>
                <td colSpan={5} className="audit-loading-cell">
                  <div className="audit-spinner" />
                  {t('auditLog.loadingLogs')}
                </td>
              </tr>
            )}
            {!loading && logs.length === 0 && !error && (
              <tr>
                <td colSpan={5} className="audit-empty-cell">{t('auditLog.empty')}</td>
              </tr>
            )}
            {logs.map(log => {
              const meta = ACTION_META[log.action] || { label: log.action, color: '#6b7280' };
              const actionLabel = t(`auditLog.actions.${log.action}`);
              const displayLabel = actionLabel.startsWith('auditLog.actions.') ? meta.label : actionLabel;
              const detailStr = formatDetails(log.action, log.details);
              const isExpanded = expandedRow === log.id;
              const actorName = log.display_name || log.username || `User #${log.user_id}`;

              return (
                <React.Fragment key={log.id}>
                  <tr
                    className={`audit-row${isExpanded ? ' expanded' : ''}`}
                    onClick={() => setExpandedRow(isExpanded ? null : log.id)}
                  >
                    <td className="audit-cell-time">{formatTimestamp(log.created_at)}</td>
                    <td className="audit-cell-user">
                      <span className="audit-user-avatar">{actorName.charAt(0).toUpperCase()}</span>
                      {actorName}
                    </td>
                    <td className="audit-cell-action">
                      <span
                        className="audit-action-badge"
                        style={{ '--badge-color': meta.color }}
                      >
                        {displayLabel}
                      </span>
                    </td>
                    <td className="audit-cell-details">
                      {detailStr || <span className="audit-no-details">—</span>}
                    </td>
                    <td className="audit-cell-ip">{log.ip_address || '—'}</td>
                  </tr>
                  {isExpanded && (
                    <tr className="audit-row-expanded-details">
                      <td colSpan={5}>
                        <div className="audit-json-wrapper">
                          <span className="audit-json-label">{t('auditLog.rawDetails')}</span>
                          <pre className="audit-json">{JSON.stringify(log.details, null, 2)}</pre>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="audit-pagination">
        <span className="audit-total-count">
          {t('auditLog.totalEntries', { total: total.toLocaleString() })}
        </span>
        <div className="audit-page-controls">
          <button
            className="audit-page-btn"
            onClick={() => setPage(0)}
            disabled={page === 0 || loading}
          >
            ««
          </button>
          <button
            className="audit-page-btn"
            onClick={() => setPage(p => Math.max(0, p - 1))}
            disabled={page === 0 || loading}
          >
            {t('auditLog.prev')}
          </button>
          <span className="audit-page-indicator">
            {t('auditLog.page', { page: page + 1, total: totalPages })}
          </span>
          <button
            className="audit-page-btn"
            onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
            disabled={page >= totalPages - 1 || loading}
          >
            {t('auditLog.next')}
          </button>
          <button
            className="audit-page-btn"
            onClick={() => setPage(totalPages - 1)}
            disabled={page >= totalPages - 1 || loading}
          >
            »»
          </button>
        </div>
      </div>
    </div>
  );
}

export default AuditLog;
